import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  GlobalStyles,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomScrollbars from "../CustomScrollbars";
import ColorizedLogText from "./ColorizedLogText";
import LogRecordList from "./LogRecordList";
import {
  useLazyGetJobFileLogsQuery,
  useLazyGetJobLogRecordsQuery,
  useLazyGetJobStatusLogsQuery,
  useLazyGetTaskFileLogsQuery,
  useLazyGetTaskLogRecordsQuery,
  useLazyGetTaskStatusLogsQuery,
} from "../../features/adminUsers/adminUsers";
import { useAppSelector } from "../../features/store";
import { botsManagementWs, BotsManagementWsEvent } from "../../features/ws/botsManagementWs";
import {
  countsForStream,
  LogRecordDto,
  LogRecordsResponse,
  LogState,
  LogStream,
} from "./logRecords";

export type LogsScope =
  | { kind: "job"; jobId: number }
  | { kind: "task"; taskId: number };

const PAGE_SIZE = 300;
const MAX_REFRESH_LIMIT = 2000;
const AUTO_REFRESH_MS = 30_000;

const TABS: Array<{ value: LogStream; label: string }> = [
  { value: "all", label: "All logs" },
  { value: "file", label: "File logs" },
  { value: "status", label: "Status logs" },
];

const FILTERS: Array<{ value: LogState; label: string }> = [
  { value: "all", label: "All" },
  { value: "error", label: "Errors" },
  { value: "success", label: "Success" },
  { value: "warning", label: "Warnings" },
  { value: "resolved", label: "Resolved" },
];

const LOG_SURFACE_SX = {
  bgcolor: "#ffffff",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  display: "flex",
  overflow: "hidden",
};

// HighlightedText marks live under this class; the file-preview provider defines
// it too, and this viewer is mounted outside that tree.
const searchMarkStyles = (
  <GlobalStyles
    styles={{
      "mark.preview-search-mark": {
        backgroundColor: "#ffe066",
        color: "inherit",
        padding: "0 1px",
        borderRadius: 2,
      },
    }}
  />
);

function CountChip({
  label,
  value,
  color,
  tooltip,
}: {
  label: string;
  value: number;
  color?: "success" | "error" | "warning" | "default";
  tooltip?: string;
}) {
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      color={value > 0 ? color ?? "default" : "default"}
      label={`${label} ${value}`}
      sx={{ height: 22, fontSize: "0.7rem" }}
    />
  );
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

/**
 * The logs of a job (or of one task) as the state they are in now.
 *
 * Three tabs over the two curated streams, each searchable, filterable by state
 * and counted over the whole scope rather than the page on screen. A failure a
 * later run fixed is shown as the success that fixed it, so "Errors 0" means
 * nothing is outstanding - which is the question an operator actually has. The
 * Raw switch (super admins) falls back to the unprocessed text the bot wrote.
 */
export default function LogsView({
  scope,
  height = { xs: 340, sm: 420, md: 520 },
}: {
  scope: LogsScope;
  height?: number | Record<string, number>;
}) {
  const isSuperUser = Boolean(
    useAppSelector((s) => s.auth.user?.platform_profile?.is_superuser),
  );

  const [fetchJobRecords] = useLazyGetJobLogRecordsQuery();
  const [fetchTaskRecords] = useLazyGetTaskLogRecordsQuery();
  const [fetchJobStatusRaw] = useLazyGetJobStatusLogsQuery();
  const [fetchJobFileRaw] = useLazyGetJobFileLogsQuery();
  const [fetchTaskStatusRaw] = useLazyGetTaskStatusLogsQuery();
  const [fetchTaskFileRaw] = useLazyGetTaskFileLogsQuery();

  const [stream, setStream] = useState<LogStream>("all");
  const [stateFilter, setStateFilter] = useState<LogState>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [records, setRecords] = useState<LogRecordDto[]>([]);
  const [meta, setMeta] = useState<LogRecordsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raw, setRaw] = useState(false);
  const [rawText, setRawText] = useState<string>("");
  const [rawLoading, setRawLoading] = useState(false);

  const jobId = scope.kind === "job" ? scope.jobId : null;
  const taskId = scope.kind === "task" ? scope.taskId : null;
  const loadedRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const runQuery = useCallback(
    async (params: { offset: number; limit: number; refresh: boolean }) => {
      const common = {
        stream,
        state: stateFilter,
        q: search || undefined,
        order: "desc" as const,
        ...params,
      };
      return jobId != null
        ? fetchJobRecords({ jobId, ...common }, false).unwrap()
        : fetchTaskRecords({ taskId: taskId as number, ...common }, false).unwrap();
    },
    [fetchJobRecords, fetchTaskRecords, jobId, taskId, search, stateFilter, stream],
  );

  const load = useCallback(
    async (mode: "reset" | "more" | "silent", refresh = false) => {
      if (mode === "more") setLoadingMore(true);
      if (mode === "reset") setLoading(true);
      try {
        const offset = mode === "more" ? loadedRef.current : 0;
        const limit =
          mode === "silent"
            ? Math.min(Math.max(loadedRef.current, PAGE_SIZE), MAX_REFRESH_LIMIT)
            : PAGE_SIZE;
        const answer = await runQuery({ offset, limit, refresh });
        setMeta(answer);
        setError(null);
        setRecords((prev) => {
          const next = mode === "more" ? [...prev, ...answer.records] : answer.records;
          loadedRef.current = next.length;
          return next;
        });
      } catch {
        if (mode !== "silent") {
          setError("Failed to load logs.");
          if (mode === "reset") {
            setRecords([]);
            loadedRef.current = 0;
          }
        }
      } finally {
        if (mode === "more") setLoadingMore(false);
        if (mode === "reset") setLoading(false);
      }
    },
    [runQuery],
  );

  // Filters, search and scope all restart the list from the first page: `load` is
  // rebuilt exactly when one of them changes.
  useEffect(() => {
    loadedRef.current = 0;
    void load("reset");
  }, [load]);

  // Quiet re-pull while the viewer is open: the counters and the visible window
  // stay current without a spinner or a scroll jump.
  useEffect(() => {
    const timer = setInterval(() => {
      void load("silent");
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // WS fast path: refresh shortly after the bot reports something for this
  // scope instead of waiting out the 30s tick. The tick above stays as the
  // slow fallback (the records live in S3 - no event maps to them one to
  // one); events that do not name this scope are ignored.
  const wsReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const unsubscribe = botsManagementWs.subscribe((event: BotsManagementWsEvent) => {
      const matchesTask = taskId != null && event.task?.task_id === taskId;
      const matchesJob =
        jobId != null &&
        (event.job?.job_id === jobId || event.task?.job_id === jobId);
      if (!matchesTask && !matchesJob) return;
      if (wsReloadTimerRef.current) return;
      wsReloadTimerRef.current = setTimeout(() => {
        wsReloadTimerRef.current = null;
        void load("silent");
      }, 7000);
    });
    return () => {
      unsubscribe();
      if (wsReloadTimerRef.current) {
        clearTimeout(wsReloadTimerRef.current);
        wsReloadTimerRef.current = null;
      }
    };
  }, [jobId, taskId, load]);

  const loadRaw = useCallback(async () => {
    setRawLoading(true);
    try {
      const wanted: Array<"status" | "file"> =
        stream === "all" ? ["status", "file"] : [stream];
      const chunks: string[] = [];
      for (const kind of wanted) {
        const answer =
          jobId != null
            ? kind === "file"
              ? await fetchJobFileRaw(jobId, false).unwrap()
              : await fetchJobStatusRaw(jobId, false).unwrap()
            : kind === "file"
              ? await fetchTaskFileRaw(taskId as number, false).unwrap()
              : await fetchTaskStatusRaw(taskId as number, false).unwrap();
        chunks.push(
          `--- ${kind === "file" ? "File logs" : "Status logs"} ---\n${answer.logs}`,
        );
      }
      setRawText(chunks.join("\n\n"));
    } catch {
      setRawText("(failed to load logs)");
    } finally {
      setRawLoading(false);
    }
  }, [
    fetchJobFileRaw,
    fetchJobStatusRaw,
    fetchTaskFileRaw,
    fetchTaskStatusRaw,
    jobId,
    taskId,
    stream,
  ]);

  useEffect(() => {
    if (!raw) return;
    void loadRaw();
  }, [raw, loadRaw]);

  const counts = useMemo(
    () => countsForStream(meta?.counts, stream),
    [meta?.counts, stream],
  );
  const range = meta?.range;
  const showTaskBadges = jobId != null;
  const outside = meta?.counts?.outside_ranges ?? 0;
  const unattributed = meta?.counts?.unattributed ?? 0;
  const fromDeleted = meta?.counts?.from_deleted_tasks ?? 0;

  const refresh = () => {
    if (raw) {
      void loadRaw();
      return;
    }
    loadedRef.current = 0;
    void load("reset", true);
  };

  const rangeLabel = (() => {
    if (!range) return "";
    if (range.ranges_pending) return taskId != null ? "Range -" : "Employees -";
    if (range.from == null || range.to == null) return "";
    const window = `${range.from}-${range.to}`;
    if (taskId != null) return `Range ${window}`;
    return range.total_employees != null
      ? `Employees ${window} of ${range.total_employees}`
      : `Employees ${window}`;
  })();

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {searchMarkStyles}
      <Tabs
        value={stream}
        onChange={(_event: React.SyntheticEvent, value: LogStream) => setStream(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            sx={{ minHeight: 40, textTransform: "none" }}
          />
        ))}
      </Tabs>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search employee, file, reason..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ minWidth: { xs: "auto", sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <ClearIcon
                  fontSize="small"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSearchInput("")}
                />
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
          {FILTERS.filter(
            (filter) =>
              filter.value !== "warning" ||
              counts.warning > 0 ||
              stateFilter === "warning",
          ).map((filter) => (
            <Chip
              key={filter.value}
              size="small"
              label={filter.label}
              onClick={() => setStateFilter(filter.value)}
              color={stateFilter === filter.value ? "primary" : "default"}
              variant={stateFilter === filter.value ? "filled" : "outlined"}
              sx={{ height: 24, fontSize: "0.72rem" }}
            />
          ))}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        {isSuperUser ? (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={raw}
                onChange={(event) => setRaw(event.target.checked)}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Raw
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        ) : null}
        <Button
          size="small"
          startIcon={
            loading || rawLoading ? <CircularProgress size={14} /> : <RefreshIcon />
          }
          disabled={loading || rawLoading}
          onClick={refresh}
        >
          Refresh
        </Button>
      </Stack>

      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
        <CountChip label="Total" value={counts.total} />
        <CountChip label="Success" value={counts.success} color="success" />
        <CountChip
          label="Errors"
          value={counts.error}
          color="error"
          tooltip="Failures nothing has fixed yet"
        />
        {counts.warning > 0 ? (
          <CountChip label="Warnings" value={counts.warning} color="warning" />
        ) : null}
        {counts.resolved > 0 ? (
          <CountChip
            label="Fixed"
            value={counts.resolved}
            color="success"
            tooltip="Failed on an earlier run and completed since - not counted as errors"
          />
        ) : null}
        {counts.unknown > 0 ? (
          <CountChip
            label="Unrecognised"
            value={counts.unknown}
            tooltip="Records this viewer could not read; open one to see the raw text"
          />
        ) : null}
        {outside > 0 ? (
          <CountChip
            label="Outside ranges"
            value={outside}
            tooltip="Records for employee numbers no task of this job covers any more"
          />
        ) : null}
        {unattributed > 0 ? (
          <CountChip
            label="Unattributed"
            value={unattributed}
            tooltip="Records that could not be tied to an employee number"
          />
        ) : null}
        {fromDeleted > 0 ? (
          <CountChip
            label="From deleted tasks"
            value={fromDeleted}
            tooltip="Records written by tasks this job no longer has - still counted as its work"
          />
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        {rangeLabel ? (
          <Typography variant="caption" sx={{ color: "text.secondary", alignSelf: "center" }}>
            {rangeLabel}
          </Typography>
        ) : null}
      </Stack>

      {range?.notice ? (
        <Typography variant="caption" sx={{ color: "warning.dark" }}>
          {range.notice}
        </Typography>
      ) : null}
      {error ? (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {error}
        </Typography>
      ) : null}

      <Box sx={{ ...LOG_SURFACE_SX, height }}>
        <CustomScrollbars>
          {raw ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                fontFamily: "monospace",
                fontSize: "0.78rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {rawLoading ? "(loading...)" : <ColorizedLogText text={rawText} />}
            </Box>
          ) : (
            <LogRecordList
              records={records}
              query={search}
              showTask={showTaskBadges}
              loading={loading}
              emptyLabel={
                meta?.counts?.total
                  ? "No records match the filters."
                  : "No log records yet."
              }
            />
          )}
        </CustomScrollbars>
      </Box>

      {!raw ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {`Showing ${records.length} of ${meta?.total_matched ?? 0}`}
          </Typography>
          {meta?.has_more ? (
            <Button
              size="small"
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
              onClick={() => void load("more")}
            >
              Load more
            </Button>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          {meta?.source ? (
            <Tooltip title={`Read at ${meta.generated_at}`}>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                {meta.source === "s3_artifacts" ? "stored logs" : "local logs"}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}
